/**
 * Phase 2 Slice 2A — GBP Candidate Picker
 *
 * Displayed when the /api/public/gbp-candidates endpoint returns
 * requires_picker=true. Shows up to 5 credible candidates (strongest three
 * first, with "Show more matches" for the rest) plus a "None of these"
 * escape hatch. Selection returns the picked place_id to the parent.
 *
 * Copy is customer-facing — no "canonical", "similarity", "algorithmic"
 * language surfaces to the visitor.
 */
import React, { useState, useEffect, useRef } from "react";
import { CheckCircle, MapPin, Phone, Storefront, X } from "@phosphor-icons/react";

function ConfidenceBadge({ confidence }) {
  if (!confidence) return null;
  const map = {
    HIGH: { label: "Strong match", bg: "bg-emerald-100", text: "text-emerald-800" },
    MEDIUM: { label: "Possible match", bg: "bg-amber-100", text: "text-amber-800" },
    LOW: { label: "Uncertain", bg: "bg-zinc-100", text: "text-zinc-700" },
  };
  const m = map[confidence] || map.LOW;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-mono font-bold uppercase tracking-wide ${m.bg} ${m.text}`}
      data-testid={`candidate-confidence-${confidence.toLowerCase()}`}
    >
      {m.label}
    </span>
  );
}

function CandidateCard({ candidate, selected, onSelect, index }) {
  const displayLoc = [candidate.city, candidate.state].filter(Boolean).join(", ")
    || candidate.formatted_address;
  return (
    <button
      type="button"
      onClick={() => onSelect(candidate.place_id)}
      className={
        "w-full text-left rounded-lg border transition p-4 focus:outline-none focus:ring-2 focus:ring-[#2D5BFF] " +
        (selected
          ? "border-[#2D5BFF] bg-blue-50/40 ring-2 ring-[#2D5BFF]"
          : "border-zinc-200 bg-white hover:border-zinc-400 hover:bg-zinc-50")
      }
      data-testid={`candidate-card-${index}`}
      aria-pressed={selected}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <div className="font-display font-bold text-zinc-900 text-base leading-snug truncate">
              {candidate.business_name}
            </div>
            {selected && (
              <CheckCircle size={18} weight="fill" className="text-[#2D5BFF] shrink-0" aria-hidden="true" />
            )}
          </div>
          {displayLoc && (
            <div className="text-sm text-zinc-700 flex items-center gap-1.5" data-testid={`candidate-address-${index}`}>
              <MapPin size={13} weight="bold" className="shrink-0 text-zinc-500" aria-hidden="true" />
              <span className="truncate">{candidate.formatted_address || displayLoc}</span>
            </div>
          )}
          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5 text-xs text-zinc-600">
            {candidate.primary_category && (
              <span className="inline-flex items-center gap-1" data-testid={`candidate-category-${index}`}>
                <Storefront size={12} weight="bold" aria-hidden="true" />
                {candidate.primary_category}
              </span>
            )}
            {candidate.phone_last4 && (
              <span className="inline-flex items-center gap-1" data-testid={`candidate-phone-${index}`}>
                <Phone size={12} weight="bold" aria-hidden="true" />
                ***-{candidate.phone_last4}
              </span>
            )}
          </div>
          {Array.isArray(candidate.match_reasons) && candidate.match_reasons.length > 0 && (
            <ul className="text-[11px] text-zinc-500 mt-2 flex flex-wrap gap-x-3 gap-y-0.5" data-testid={`candidate-reasons-${index}`}>
              {candidate.match_reasons.map((reason, i) => (
                <li key={i}>· {reason}</li>
              ))}
            </ul>
          )}
        </div>
        <ConfidenceBadge confidence={candidate.confidence} />
      </div>
    </button>
  );
}

/**
 * Modal / inline panel that presents candidates and captures the visitor's pick.
 * Props:
 *   - candidates: array of candidate dicts from POST /api/public/gbp-candidates
 *   - onSelect(place_id): called when user picks a listing
 *   - onNoneMatch(): called when user picks "None of these are my business"
 *   - onCancel(): called when user closes the modal without picking
 *   - loading: while parent is processing the selection
 */
export default function GbpCandidatePicker({ candidates, onSelect, onNoneMatch, onCancel, loading = false }) {
  const [selectedId, setSelectedId] = useState(null);
  const [showMore, setShowMore] = useState(false);
  const firstButtonRef = useRef(null);

  useEffect(() => {
    // Return focus into the modal for keyboard users.
    if (firstButtonRef.current) firstButtonRef.current.focus();
    const onKey = (e) => {
      if (e.key === "Escape" && !loading) {
        onCancel && onCancel();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [loading, onCancel]);

  if (!Array.isArray(candidates) || candidates.length === 0) {
    return null;
  }

  const initial = candidates.slice(0, 3);
  const extras = candidates.slice(3);
  const visible = showMore ? candidates : initial;

  const handleConfirm = () => {
    if (!selectedId || loading) return;
    onSelect && onSelect(selectedId);
  };

  return (
    <div
      className="fixed inset-0 z-[95] bg-zinc-900/60 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="candidate-picker-title"
      data-testid="gbp-candidate-picker"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="p-5 sm:p-6 border-b border-zinc-200 flex items-start justify-between gap-4">
          <div>
            <div className="label-tiny text-[#2D5BFF] mb-1.5">Confirm Your Business</div>
            <h2
              id="candidate-picker-title"
              className="font-display text-xl sm:text-2xl font-black text-zinc-900 leading-tight"
            >
              Which Google listing belongs to your business?
            </h2>
            <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
              We found more than one possible match. Choose the listing that is yours
              so the assessment measures the right business.
            </p>
          </div>
          <button
            type="button"
            onClick={() => !loading && onCancel && onCancel()}
            disabled={loading}
            aria-label="Close"
            className="shrink-0 text-zinc-500 hover:text-zinc-900 disabled:opacity-50"
            data-testid="candidate-picker-close"
          >
            <X size={22} weight="bold" />
          </button>
        </div>

        <div className="p-5 sm:p-6 overflow-y-auto flex-1 space-y-3" ref={firstButtonRef} tabIndex={-1}>
          {visible.map((c, i) => (
            <CandidateCard
              key={c.place_id || i}
              candidate={c}
              index={i}
              selected={selectedId === c.place_id}
              onSelect={(pid) => setSelectedId(pid)}
            />
          ))}

          {!showMore && extras.length > 0 && (
            <button
              type="button"
              onClick={() => setShowMore(true)}
              className="w-full text-sm text-zinc-600 hover:text-zinc-900 py-2 border border-dashed border-zinc-300 rounded-lg hover:bg-zinc-50 transition"
              data-testid="candidate-show-more"
            >
              Show {extras.length} more {extras.length === 1 ? "match" : "matches"}
            </button>
          )}

          <button
            type="button"
            onClick={() => !loading && onNoneMatch && onNoneMatch()}
            disabled={loading}
            className="w-full text-sm text-zinc-700 hover:text-zinc-900 py-3 border border-zinc-300 rounded-lg hover:bg-zinc-100 transition disabled:opacity-50 mt-3"
            data-testid="candidate-none-of-these"
          >
            None of these are my business
          </button>
        </div>

        <div className="p-5 sm:p-6 border-t border-zinc-200 flex items-center justify-between gap-3 bg-zinc-50/60">
          <p className="text-xs text-zinc-500 leading-snug">
            Your selection will only be used for this assessment.
          </p>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!selectedId || loading}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-bold text-sm text-white hover:opacity-90 transition disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: "#2D5BFF" }}
            data-testid="candidate-confirm-btn"
          >
            {loading ? (<><span className="spinner" /> Working…</>) : (<>Confirm and continue</>)}
          </button>
        </div>
      </div>
    </div>
  );
}
